import { useReducer, type ChangeEventHandler } from 'react';
import translations from '../../../translations';
import { isNullOrUndefined } from '../../../utils/guard';
import { stringReplace } from '../../../utils/string';
import { importFlow, importFlow2, importSharedFlow } from '../../../sources/flow';
import { useTabs, useTabsDispatch } from '../../tabs/TabsProvider';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import type { PackageConflictResponse } from '../../../types/flow';
import { useTenant } from '../../TenantProvider';

const useFlowImport = (onImportComplete?: () => void) => {
    const [state, dispatchImportExport] = useReducer(reducer, {
        isProcessing: false,
        importedJSON: null,
        importToken: null,
        showImportConfirmation: false,
        conflictedImportDependents: null,
    });

    const {
        isProcessing,
        importedJSON,
        importToken,
        showImportConfirmation,
        conflictedImportDependents,
    } = state;

    const tenant = useTenant();
    const { tabs } = useTabs();
    const dispatchTabs = useTabsDispatch();
    const dispatchNotification = useNotificationDispatch();

    const onChangeImportToken: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
        dispatchImportExport({
            type: 'importTokenUpdated',
            payload: value,
        });
    };

    const onDrop = (files: File[]) => {
        for (const file of files) {
            const isBinaryPackage = file.name.endsWith('.package2');

            if (isBinaryPackage) {
                // For binary packages, use the File object directly as it extends Blob
                dispatchImportExport({
                    type: 'fileDropped',
                    payload: file,
                });
            } else {
                // For JSON packages, read as text
                const reader = new FileReader();
                reader.onloadend = (event) => {
                    dispatchImportExport({
                        type: 'fileDropped',
                        payload: event.target?.result?.toString() ?? null,
                    });
                };
                reader.readAsText(file);
            }
        }
    };

    const onImportConfirmed = async (force = false) => {
        if (!importedJSON && !importToken) {
            return;
        }

        try {
            dispatchImportExport({
                type: 'setIsProcessing',
                payload: true,
            });

            const result = importedJSON
                ? typeof importedJSON !== 'string'
                    ? await importFlow2(importedJSON as Blob, force)
                    : await importFlow(importedJSON as string, force)
                : importToken
                  ? await importSharedFlow(importToken, force)
                  : null;

            if (!result) {
                return;
            }

            dispatchImportExport({
                type: 'dismissImportConfirmation',
            });

            // Check for an open tab tab that has a matching id and ensure the name is correct
            // An import will rename a flow if the ids match but the names are different
            const existingTab = tabs.find(
                (tab) => tab.type === 'flow' && tab.elementId === result.id.id,
            );

            if (existingTab && existingTab.title !== result.developerName) {
                const { key } = existingTab;
                dispatchTabs({
                    type: 'SET_TITLE',
                    payload: { tabKey: key, title: result.developerName },
                });
            }

            onImportComplete?.();

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: stringReplace(
                        translations.IMPORT_EXPORT_import_success_message,
                        result.developerName,
                    ),
                },
            });
        } catch (error) {
            const conflict = error as {
                cause: {
                    wasConflict: boolean;
                    conflictingDependents: PackageConflictResponse;
                };
            };
            if (conflict.cause?.wasConflict) {
                dispatchImportExport({
                    type: 'importConflictResponse',
                    payload: conflict.cause.conflictingDependents,
                });
            } else {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        }
    };

    const onDismissConfirmation = () => dispatchImportExport({ type: 'dismissImportConfirmation' });

    const importedFlowName = getNameFromFlowPackage(importedJSON as string);

    const confirmFlowImportMessage =
        isNullOrUndefined(importedFlowName) === false
            ? stringReplace(
                  // If there is a Flow name found, then show the flow name import text
                  translations.IMPORT_confirm_message_with_flow_name,
                  importedFlowName,
                  tenant.developerName,
              )
            : stringReplace(
                  // Otherwise show the import text without the flow name
                  translations.IMPORT_confirm_message,
                  tenant.developerName,
              );

    const onImportToken = () =>
        dispatchImportExport({
            type: 'importSharingTokenClicked',
        });

    const showConflictedImportDependents = conflictedImportDependents !== null;

    return {
        isProcessing,
        importToken,
        showImportConfirmation,
        conflictedImportDependents,
        showConflictedImportDependents,
        confirmFlowImportMessage,
        onImportConfirmed,
        onChangeImportToken,
        onDrop,
        onImportToken,
        onDismissConfirmation,
    };
};

export default useFlowImport;

function getNameFromFlowPackage(flowJSON: string | null) {
    if (!flowJSON || typeof flowJSON !== 'string') {
        return null;
    }

    try {
        let flowPackage: { flowConfiguration: { developerName: string } } | string =
            JSON.parse(flowJSON);

        // flowJSON may be double encoded, so need to check to see if it is a string or an object
        if (typeof flowPackage === 'string') {
            flowPackage = JSON.parse(flowPackage);
        }

        if (
            typeof flowPackage === 'object' &&
            Object.hasOwn(flowPackage, 'flowConfiguration') &&
            Object.hasOwn(flowPackage.flowConfiguration, 'developerName')
        ) {
            return flowPackage.flowConfiguration.developerName;
        }
    } catch {
        return null;
    }
    return null;
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'setIsProcessing':
            return {
                ...state,
                isProcessing: action.payload,
            };
        case 'fileDropped':
            return {
                ...state,
                importedJSON: action.payload,
                showImportConfirmation: true,
            };
        case 'importTokenUpdated':
            return {
                ...state,
                importToken: action.payload,
            };
        case 'importSharingTokenClicked':
            return {
                ...state,
                showImportConfirmation: true,
            };
        case 'dismissImportConfirmation':
            return {
                ...state,
                showImportConfirmation: false,
                conflictedImportDependents: null,
                importedJSON: null,
                isProcessing: false,
                importToken: null,
            };
        case 'importConflictResponse':
            return {
                ...state,
                conflictedImportDependents: action.payload,
                isProcessing: false,
            };
    }
}

type State = {
    isProcessing: boolean;
    importedJSON: string | Blob | null;
    importToken: string | null;
    showImportConfirmation: boolean;
    conflictedImportDependents: PackageConflictResponse | null;
};

type Action =
    | {
          type: 'setIsProcessing';
          payload: boolean;
      }
    | {
          type: 'fileDropped';
          payload: string | Blob | null;
      }
    | {
          type: 'importTokenUpdated';
          payload: string | null;
      }
    | {
          type: 'importSharingTokenClicked';
      }
    | {
          type: 'dismissImportConfirmation';
      }
    | {
          type: 'importConflictResponse';
          payload: PackageConflictResponse;
      };
